import type { ComponentProps, FC, ReactNode } from 'react';
import React, { memo, useCallback, useEffect } from 'react';

import { AudioContentProvider } from '@confluence/audio/entry-points/AudioContentProvider.tsx';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import {
	SPACE_OVERVIEW_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
	VIEW_ALL_MACRO_EXPERIENCE,
	FRONT_COVER_LOAD_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { routeNameToContentTypeMap } from '@confluence/experience-tracker/entry-points/startExperienceForRoute.ts';
import {
	getExperienceTracker,
	stopExperienceOnError,
} from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { usePageState } from '@confluence/page-context';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { ContentContainer } from '@confluence/content-container/entry-points/ContentContainer.ts';
import {
	ContentBlogUnifiedQueryArtifact,
	ContentUnifiedQueryArtifact,
	createContentBlogUnifiedQueryVariables,
	createContentUnifiedQueryVariables,
	type ContentBlogUnifiedQueryType,
	type ContentBlogUnifiedQueryVariablesType,
	type ContentUnifiedQueryType,
	type ContentUnifiedQueryVariablesType,
} from '@confluence/content-unified-query';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isExpectedApolloError } from '@confluence/error-boundary/entry-points/expectedGraphQlErrorHelper.ts';
import { getProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { PagePrerequisites } from '@confluence/content-prerequisites/entry-points/PagePrerequisites.ts';
import { useDocumentUpdateStatusDispatch } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';
import {
	VIEW_PAGE,
	SPACE_OVERVIEW,
	COMPANY_HUB,
} from '@confluence/named-routes/entry-points/routes.ts';
import { RENDERER } from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart.ts';
import { ContentStateContainer } from '@confluence/content-state/entry-points/useContentState.ts';
import {
	ReloadType,
	ReloadPriority,
	useQuickReloadSubscriber,
} from '@confluence/quick-reload/entry-points/subscription.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { useUnreadCommentsSetup } from '@confluence/unread-comments/entry-points/useUnreadCommentsSetup.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import type { BaseViewPageProps } from './BaseViewPageProps.ts';
import { VIEW_PAGE_CONTAINER_METRIC } from './perf.config.tsx';
import { useContentExtensionMetrics } from './useContentExtensionMetrics.ts';
import { useContentReloadOnFabricSupportChange } from './useContentReloadOnFabricSupportChange.ts';

const pageMap = {
	SPACE_OVERVIEW: {
		route: SPACE_OVERVIEW,
		errorAttribution: 'next-space-overview',
		name: SPACE_OVERVIEW_EXPERIENCE,
		isContentView: false,
	},
	COMPANY_HUB: {
		route: COMPANY_HUB,
		errorAttribution: 'next-company-hub',
		name: FRONT_COVER_LOAD_EXPERIENCE,
		isContentView: false,
	},
	page: {
		route: VIEW_PAGE,
		errorAttribution: 'next-view-page',
		name: VIEW_PAGE_EXPERIENCE,
		isContentView: true,
	},
};

type ViewPageContentQueryData = ContentUnifiedQueryType | ContentBlogUnifiedQueryType | undefined;

export type ViewPageHeaderRenderer = (contentQueryData: ViewPageContentQueryData) => ReactNode;

interface ViewPageContainerProps extends BaseViewPageProps {
	children?: (props: {
		isThemed: boolean;
		contentQueryData: ViewPageContentQueryData;
		contentQueryError: ApolloErrorInterop | undefined;
		contentQueryRefetch: () => Promise<void>;
	}) => React.ReactNode | null;
	stickyHeader?: ReactNode | ViewPageHeaderRenderer;
	contentScreenStyles?: React.CSSProperties;
	isContainerContentView?: boolean;
	versionOverride?: number | null;
	customPerfData?: Record<string, string | boolean | object>;
	isEmbeddedPage?: boolean;
	hasDocumentContractAnalyticsByline?: boolean;
	hasDocumentContractContentHeader?: boolean;
}

export const ViewPageContainer: FC<ViewPageContainerProps> = memo(
	({
		contentId,
		spaceKey,
		children,
		stickyHeader,
		contentScreenStyles,
		isContainerContentView = true,
		versionOverride = null,
		customPerfData,
		isEmbeddedPage,
		hasDocumentContractAnalyticsByline = false,
		hasDocumentContractContentHeader = false,
	}) => {
		const { setCurrentDocumentVersion, setDocumentNeedsUpdate, setPublishedDocumentVersion } =
			useDocumentUpdateStatusDispatch();
		const [{ routeName = '', contentType }] = usePageState();
		const { edition, userId } = useSessionData();
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		const isSSR = Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

		useUnreadCommentsSetup({
			pageId: contentId,
			pageContentTypeProps: {
				pageMode: 'view',
			},
		});

		const extractDataAndSetPublishedVersion = useCallback(
			({ refetchContentQueryResult }) => {
				const { data } = refetchContentQueryResult;
				if (data) {
					const versionNumber = data.content?.nodes?.[0]?.version?.number;
					if (versionNumber !== undefined && versionNumber !== null) {
						setPublishedDocumentVersion(versionNumber);
					}
				}
			},
			[setPublishedDocumentVersion],
		);

		const isBlogpost = contentType === 'blogpost';
		const shouldUseContentHeaderFragment =
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			hasDocumentContractContentHeader &&
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			fg('confluence_vp_header_frag_reader');
		const shouldUseFabricEligibilityFragment =
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			shouldUseContentHeaderFragment &&
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			fg('confluence_vp_fabric_eligibility_frag_reader');
		const skipContentQuery = !contentId || !spaceKey;

		const pageContentQueryVariables = {
			...createContentUnifiedQueryVariables({
				contentId,
				spaceKey,
				versionOverride,
				isSSR,
				// confluence-feature-gating/inline-usage: expValEquals must be inlined
				agentSessionsUserId:
					!!userId && expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)
						? userId
						: undefined,
				includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
				includeBylineContributors:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeAutomationDiscoveries: fg('cc_automations_discovery_nudges'),
				includeMetadata:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeClassification:
					!StatsigConfigurations.isKillSwitchOn(
						'confluence_vp_classification_context_frag_reader',
					) && expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeReactions:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_reactions_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeAudioContent:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_audio_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
				includeAnalyticsByline:
					hasDocumentContractAnalyticsByline &&
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_analytics_byline_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
				includeContentHeader: shouldUseContentHeaderFragment,
				includeContentHeaderAccess:
					shouldUseContentHeaderFragment &&
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeFabricEditorEligibility: shouldUseFabricEligibilityFragment,
				includeContentHeaderPublicLink:
					shouldUseContentHeaderFragment &&
					!isBlogpost &&
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
			}),
			...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_mode_frag_reader') &&
			expVal('confluence_exp_vp_document', 'isEnabled', false)
				? { useContentModeFragment: true }
				: {}),
		};
		const blogContentQueryVariables = {
			...createContentBlogUnifiedQueryVariables({
				contentId,
				spaceKey,
				versionOverride,
				isSSR,
				includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
				includeBylineContributors:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeAutomationDiscoveries: fg('cc_automations_discovery_nudges'),
				includeMetadata:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeClassification:
					!StatsigConfigurations.isKillSwitchOn(
						'confluence_vp_classification_context_frag_reader',
					) && expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeReactions:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_reactions_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeAudioContent:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_audio_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
				includeAnalyticsByline:
					hasDocumentContractAnalyticsByline &&
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_analytics_byline_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
				includeContentHeader: shouldUseContentHeaderFragment,
				includeContentHeaderAccess:
					shouldUseContentHeaderFragment &&
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeFabricEditorEligibility: shouldUseFabricEligibilityFragment,
				includeContentHeaderPublicLink: false,
			}),
			...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_mode_frag_reader') &&
			expVal('confluence_exp_vp_document', 'isEnabled', false)
				? { useContentModeFragment: true }
				: {}),
		};

		const pageContentQueryResult = useQueryInterop<
			ContentUnifiedQueryType,
			ContentUnifiedQueryVariablesType
		>(ContentUnifiedQueryArtifact, {
			fetchPolicy: 'cache-first',
			context: { single: true },
			skip: skipContentQuery || isBlogpost,
			variables: pageContentQueryVariables,
		});

		const blogContentQueryResult = useQueryInterop<
			ContentBlogUnifiedQueryType,
			ContentBlogUnifiedQueryVariablesType
		>(ContentBlogUnifiedQueryArtifact, {
			fetchPolicy: 'cache-first',
			context: { single: true },
			skip: skipContentQuery || !isBlogpost,
			variables: blogContentQueryVariables,
		});

		const contentQueryResult = isBlogpost ? blogContentQueryResult : pageContentQueryResult;

		const { route, errorAttribution, name, isContentView } = pageMap[routeName] ?? pageMap['page'];

		const contentQueryVariables = isBlogpost
			? blogContentQueryVariables
			: pageContentQueryVariables;
		const { refetch } = contentQueryResult;

		useContentExtensionMetrics(
			contentQueryResult?.data?.content?.nodes?.[0]?.body?.dynamic?.value,
			customPerfData,
		);

		useEffect(() => {
			// If navigating to a different page, always assume document is up to date.
			// Added for COMMENTS-5327, so when user jumps from an outdated page to another
			// up-to-date page, we won't disable the comment button due to outdated-ness.
			// Caveat: The destination page may not actually be up-to-date, e.g. if the
			// page content was loaded from cache that's saved from the user's previous
			// visit in the same browser tab, and a new version was published since then.
			// Invoking `setDocumentNeedsUpdate(false)` here means `documentNeedsUpdate`
			// may be `false` when page is actually outdated, but this scenario can already
			// occur before this change. COMMENTS-5816 tracks fixing this scenario altogether.
			if (!expValEquals('confluence_fe_disable_comment_if_page_outdated_v2', 'isEnabled', true)) {
				// With confluence_fe_disable_comment_if_page_outdated_v2 ON,
				// `documentNeedsUpdate` is set to true whenever we set a new
				// `publishedDocumentVersion` IF it's different from `currentDocumentVersion`.
				// This useEffect can be cleaned up after v2 is fully rolled out.
				setDocumentNeedsUpdate(false);
			}
		}, [contentId, setDocumentNeedsUpdate]);

		// Use this instead of `contentQueryResult.data?.content?.nodes?.[0]?.version?.number`
		// because that field is fetched by other queries too, and thus may become mismatched
		// with the actual version of `body` in GQL cache.
		const contentVersion =
			contentQueryResult.data?.content?.nodes?.[0]?.body?.dynamic?.content?.version?.number;

		useEffect(() => {
			if (
				contentVersion &&
				expValEquals('confluence_fe_disable_comment_if_page_outdated_v2', 'isEnabled', true)
			) {
				setCurrentDocumentVersion(contentVersion);
			}
		}, [contentVersion, setCurrentDocumentVersion]);

		const reload = useCallback(
			async () => {
				getExperienceTracker().start({
					name,
					timeout: ExperienceTimeout.PAGE_LOAD,
					attributes: {
						contentType: routeNameToContentTypeMap[routeName],
						contentId,
						isSpaceOverviewNextRendered: true,
						edition,
						quickReload: true, //tell exp metrics error came from reload and not primary experience
					},
				});

				try {
					const refetchContentQueryResult = await refetch(contentQueryVariables);

					extractDataAndSetPublishedVersion({ refetchContentQueryResult });
				} catch (error) {
					if (isUnauthorizedError(error) || isExpectedApolloError(error)) {
						markErrorAsHandled(error);
						getExperienceTracker().abort({
							name,
							reason: 'Expected Apollo error',
						});
						return; //if it's an expected GQL failure don't throw or fail the experience
					}
					stopExperienceOnError(name, error);
					throw error; //this successfully avoids the success below
				}
				getExperienceTracker().succeed({ name });
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[refetch, contentId],
		);

		const content = contentQueryResult.data?.content?.nodes?.[0];
		// Downstream packages are checked against the last generated content-unified-query declaration.
		const contentWithAudioFragment = content as typeof content & {
			readonly audioContentFragment?: ComponentProps<
				typeof AudioContentProvider
			>['contentFragmentRef'];
		};
		const fabricEditorSupported = contentQueryResult.loading
			? null
			: (content?.metadata?.frontend?.fabricEditorSupported ?? null);
		const bodyRepresentation = contentQueryResult.loading
			? null
			: (content?.body?.dynamic?.representation ?? null);
		useContentReloadOnFabricSupportChange({
			contentId,
			fabricEditorSupported,
			bodyRepresentation,
			reload,
		});

		useQuickReloadSubscriber({
			name: 'content',
			types: [ReloadType.content, ReloadType.comment],
			reload,
		});

		useQuickReloadSubscriber({
			name: 'commentsDataManager',
			types: [ReloadType.comment],
			reload: async () => {
				await getProcessedCommentsDataState().refetch?.();
			},
			priority: ReloadPriority.background,
		});

		const resolvedStickyHeader =
			typeof stickyHeader === 'function' ? stickyHeader(contentQueryResult.data) : stickyHeader;

		return (
			<>
				<PageSegmentLoadStart key={contentId} metric={VIEW_PAGE_CONTAINER_METRIC} />
				<MacroExperienceStart
					contentId={contentId}
					mode={RENDERER}
					name={VIEW_ALL_MACRO_EXPERIENCE}
					timeout={ExperienceTimeout.MACRO_LOAD}
				/>
				<PagePrerequisites
					route={route}
					contentQueryResult={contentQueryResult}
					versionOverride={versionOverride}
				>
					<AudioContentProvider
						contentId={contentId}
						contentFragmentRef={contentWithAudioFragment?.audioContentFragment}
					>
						<ContentStateContainer scope={contentId}>
							<ContentContainer
								fetchTheme
								contentThemeSpaceData={
									contentQueryResult.data ? (contentQueryResult.data.space ?? null) : undefined
								}
								contentId={contentId}
								spaceKey={spaceKey}
								key="content-screen"
								errorAttribution={errorAttribution}
								isScreenContentView={isContentView}
								isContainerContentView={isContainerContentView}
								contentScreenStyles={contentScreenStyles}
								stickyHeader={resolvedStickyHeader}
								versionOverride={versionOverride}
								isEmbeddedPage={isEmbeddedPage}
							>
								{({ isThemed }) => (
									<article>
										{children &&
											children({
												isThemed,
												contentQueryData: contentQueryResult.data,
												contentQueryError: contentQueryResult.error,
												contentQueryRefetch: reload,
											})}
										<PageSegmentLoadEnd
											key={contentId}
											metric={VIEW_PAGE_CONTAINER_METRIC}
											customData={{ isThemed }}
										/>
									</article>
								)}
							</ContentContainer>
						</ContentStateContainer>
					</AudioContentProvider>
				</PagePrerequisites>
			</>
		);
	},
);
